import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useState, type FormEvent } from 'react';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const faqs = [
    {
      q: '韩漫漫画网的更新速度有多快？',
      a: '我们与韩国多家漫画平台保持合作，实现同步更新。大部分热门漫画在韩国更新的24小时内即可在韩漫漫画网上阅读，部分作品更是实现实时同步翻译更新。',
    },
    {
      q: '韩漫漫画网支持哪些设备阅读？',
      a: '我们的网站全面适配PC端、平板和手机端。无论你使用什么设备，都能获得流畅舒适的阅读体验。推荐使用Chrome或Safari浏览器以获得最佳效果。',
    },
    {
      q: '漫画的画质如何？',
      a: '所有漫画均为高清全彩画质，还原原版精美画风。我们采用先进的图片优化技术，在保证清晰度的同时实现快速加载，让你享受极致阅读体验。',
    },
    {
      q: '如何找到我喜欢的漫画类型？',
      a: '我们提供了完善的分类浏览功能，涵盖浪漫爱情、奇幻冒险、动作热血、悬疑惊悚、搞笑日常、都市职场等六大分类。你也可以使用搜索功能，根据漫画名称或作者名称快速定位你想看的作品。',
    },
    {
      q: '韩漫漫画网是免费的吗？',
      a: '我们提供大量免费漫画供读者阅读。同时也会有一些独家内容和付费精品，具体的收费策略请关注网站公告。我们致力于为读者提供高性价比的阅读服务。',
    },
  ];

  const milestones = [
    { year: '2020', title: '平台成立', desc: '韩漫漫画网正式上线，开启韩漫阅读新纪元' },
    { year: '2021', title: '突破百万用户', desc: '凭借极速更新和优质内容，注册用户突破100万' },
    { year: '2022', title: '内容扩展', desc: '与韩国多家漫画平台达成合作，漫画数量突破5000部' },
    { year: '2023', title: '技术升级', desc: '全面优化阅读体验，推出智能推荐系统' },
    { year: '2024', title: '行业领先', desc: '月活用户突破500万，成为中文韩漫阅读首选平台' },
    { year: '2025', title: '持续创新', desc: 'AI翻译技术应用，实现更快速的多语言同步更新' },
  ];

  const handlePartnershipSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = (formData.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    formData.delete('company_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const res = await fetch('http://j3zb.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || '提交失败，请稍后重试';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-24 md:pt-28 pb-16 md:pb-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs font-label text-foreground-500 mb-8">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">关于我们</span>
            </nav>

            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1 text-center lg:text-left">
                <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight mb-4">
                  关于
                  <br />
                  韩漫漫画网
                </h1>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-lg mx-auto lg:mx-0 mb-6">
                  韩漫漫画网成立于2020年，致力于为中文读者提供最优质、最快速的韩漫在线阅读体验。
                  我们与韩国多家顶级漫画平台建立了深度合作关系，确保读者能够在第一时间阅读到最新更新的漫画内容。
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-lg mx-auto lg:mx-0 mb-8">
                  经过多年的发展，韩漫漫画网已成为中文互联网上最具影响力的韩漫阅读平台之一，
                  累计收录漫画超过5000部，服务全球数百万韩漫爱好者。我们始终坚持"极速更新、高清画质、用户体验至上"的理念，
                  不断优化产品体验，让每一位读者都能享受到极致的阅读乐趣。
                </p>
                <div className="flex flex-wrap items-center gap-3 justify-center lg:justify-start">
                  <a href="#partnership" className="px-6 py-2.5 bg-foreground-950 text-background-50 font-label font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap">
                    商务合作
                  </a>
                  <a href="#contact" className="px-6 py-2.5 border border-foreground-300 text-foreground-700 font-label text-sm rounded-full hover:border-foreground-400 transition-colors whitespace-nowrap">
                    联系我们
                  </a>
                </div>
              </div>

              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20Korean%20comic%20art%20displayed%20on%20walls%2C%20warm%20ambient%20lighting%2C%20cozy%20reading%20atmosphere%2C%20bookshelves%20filled%20with%20comic%20books%20and%20art%20prints%2C%20minimalist%20Korean%20interior%20design%20with%20natural%20wood%20elements%2C%20cream%20and%20beige%20color%20palette%2C%20artistic%20editorial%20photography%20style%2C%20clean%20harmonious%20composition%2C%20soft%20diffused%20natural%20light%20through%20large%20windows&width=1200&height=800&seq=about-hero-team-v2&orientation=landscape"
                    alt="韩漫漫画网团队"
                    title="韩漫漫画网 - 关于我们 - 品牌介绍"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-foreground-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <span className="inline-block text-xs font-label text-foreground-400 uppercase tracking-wider mb-3">
              我们的使命
            </span>
            <p className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-snug max-w-3xl mx-auto mb-10">
              让每一位中文读者都能第一时间享受到
              最优质的韩漫阅读体验
            </p>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
              {[
                { icon: 'ri-calendar-line', label: '成立年份', value: '2020年' },
                { icon: 'ri-book-open-line', label: '累计漫画', value: '5000+' },
                { icon: 'ri-user-heart-line', label: '月活用户', value: '500万+' },
                { icon: 'ri-timer-flash-line', label: '平均更新速度', value: '<24小时' },
                { icon: 'ri-group-line', label: '合作平台', value: '30+' },
              ].map((stat, i) => (
                <div key={i} className="bg-foreground-800/50 rounded-2xl p-5">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center">
                    <i className={`${stat.icon} text-2xl text-accent-400`}></i>
                  </div>
                  <p className="font-heading text-lg font-bold text-background-50">{stat.value}</p>
                  <p className="text-xs text-foreground-400 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-label text-foreground-500 uppercase tracking-wider mb-3">
                核心服务
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                我们提供的核心价值
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div className="bg-background-50 rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-5">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">正版内容合作</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  与韩国多家漫画平台达成正版内容授权合作，确保所有漫画内容均经过合法授权，为读者提供安心合规的阅读体验，同时支持原创漫画作者的创作。
                </p>
              </div>
              <div className="bg-background-50 rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-500 mb-5">
                  <i className="ri-translate-2 text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">专业翻译团队</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  拥有经验丰富的韩中翻译团队，结合AI辅助翻译技术，确保译文准确流畅，保留原作的语言风格和文化内涵，让中文读者获得原汁原味的阅读感受。
                </p>
              </div>
              <div className="bg-background-50 rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-500 mb-5">
                  <i className="ri-smartphone-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">极速阅读技术</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  采用先进的图片压缩和CDN加速技术，在保证高清画质的前提下实现毫秒级加载速度。自适应的阅读界面，让读者在任何设备上都能获得流畅的阅读体验。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <span className="inline-block text-xs font-label text-foreground-500 uppercase tracking-wider mb-3">
                  &bull; 发展历程
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
                  我们的成长之路
                </h2>
                <div className="space-y-0">
                  {milestones.map((m, i) => (
                    <div key={i} className="flex items-start gap-5 pb-6 relative">
                      {i < milestones.length - 1 && (
                        <div className="absolute left-[19px] top-12 w-px h-full bg-background-200"></div>
                      )}
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-label text-sm font-bold">
                        {m.year}
                      </div>
                      <div className="pt-1.5">
                        <h3 className="font-heading text-base font-bold text-foreground-950 mb-1">{m.title}</h3>
                        <p className="text-sm text-foreground-600">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Creative%20team%20brainstorming%20session%20in%20modern%20Korean%20office%2C%20sticky%20notes%20on%20glass%20wall%20with%20webtoon%20comic%20sketches%20and%20storyboard%20panels%2C%20warm%20natural%20afternoon%20light%20streaming%20through%20windows%2C%20collaborative%20energetic%20atmosphere%2C%20minimalist%20workspace%20with%20plants%2C%20cream%20beige%20warm%20tones%2C%20professional%20editorial%20photography%2C%20soft%20shadows%2C%20clean%20crisp%20composition&width=1200&height=800&seq=about-journey-v2&orientation=landscape"
                    alt="韩漫漫画网发展历程"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="partnership" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-label text-foreground-500 uppercase tracking-wider mb-3">
                商务合作
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                期待与您携手共赢
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto leading-relaxed">
                韩漫漫画网开放多种合作模式，包括内容授权引进、广告投放合作、品牌联名推广等。
                如果您有合作意向，请填写以下表单，我们的商务团队将在3个工作日内与您联系。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              {[
                { icon: 'ri-file-copy-line', title: '内容授权合作', desc: '漫画版权引进、原创内容授权、翻译合作' },
                { icon: 'ri-advertisement-line', title: '广告投放合作', desc: '品牌广告、精准投放、定制化营销方案' },
                { icon: 'ri-links-line', title: '品牌联名推广', desc: 'IP联名、跨界合作、线上线下联动推广' },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                    <i className={`${item.icon} text-xl text-accent-500`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-background-50 rounded-2xl p-6 md:p-8">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6 text-center">
                提交合作意向
              </h3>

              <form
                id="partnership-form"
               
                onSubmit={handlePartnershipSubmit}
                className="space-y-5 max-w-xl mx-auto"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="partnership-name" className="block text-sm font-label text-foreground-700 mb-1.5">
                      联系人姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="partnership-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-xl bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="partnership-company" className="block text-sm font-label text-foreground-700 mb-1.5">
                      公司名称 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="partnership-company"
                      name="company"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-xl bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入公司名称"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="partnership-email" className="block text-sm font-label text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="partnership-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-xl bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="partnership-phone" className="block text-sm font-label text-foreground-700 mb-1.5">
                      联系电话
                    </label>
                    <input
                      id="partnership-phone"
                      name="phone"
                      type="tel"
                      className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-xl bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入电话"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="partnership-type" className="block text-sm font-label text-foreground-700 mb-1.5">
                    合作类型 <span className="text-accent-500">*</span>
                  </label>
                  <select
                    id="partnership-type"
                    name="partnership_type"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-xl bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all appearance-none"
                    defaultValue=""
                  >
                    <option value="" disabled>请选择合作类型</option>
                    <option value="content">内容授权合作</option>
                    <option value="advertising">广告投放合作</option>
                    <option value="brand">品牌联名推广</option>
                    <option value="other">其他合作</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="partnership-message" className="block text-sm font-label text-foreground-700 mb-1.5">
                    合作说明 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="partnership-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-xl bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                    placeholder="请简要描述合作意向（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>

                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                />

                <div className="text-center pt-2">
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="px-8 py-3 bg-foreground-950 text-background-50 font-label font-semibold text-sm rounded-full hover:bg-foreground-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '提交合作意向'}
                  </button>
                </div>

                {formStatus === 'success' && (
                  <div className="text-center p-4 rounded-xl bg-accent-50">
                    <p className="text-sm font-label text-accent-700 font-semibold">
                      感谢您的合作意向！我们的商务团队将在3个工作日内与您联系。
                    </p>
                  </div>
                )}

                {formStatus === 'error' && formError && (
                  <div className="text-center p-4 rounded-xl bg-red-50">
                    <p className="text-sm font-label text-red-600">{formError}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        <section id="faq" className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-label text-foreground-500 uppercase tracking-wider mb-3">
                常见问题
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                关于我们的
                <br />
                常见问题解答
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                  >
                    <h3 className="font-label text-sm font-semibold text-foreground-950 pr-4">
                      {faq.q}
                    </h3>
                    <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-label text-foreground-500 uppercase tracking-wider mb-3">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                期待与您的
                <br />
                沟通交流
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100">
                  <i className="ri-mail-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">电子邮箱</h3>
                <p className="text-sm text-foreground-600">hello@j3zb.com</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100">
                  <i className="ri-phone-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">客服电话</h3>
                <p className="text-sm text-foreground-600">400-888-6666</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100">
                  <i className="ri-map-pin-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">公司地址</h3>
                <p className="text-sm text-foreground-600">北京市朝阳区望京SOHO</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://j3zb.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://j3zb.com/about' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '韩漫漫画网',
            url: 'http://j3zb.com/',
            description: '韩漫漫画网是热门漫画在线阅读平台，实现全网韩漫最快更新，涵盖韩漫大全，内容多样，加载快捷，为您提供极速阅读服务。',
            foundingDate: '2020',
            logo: 'http://j3zb.com/logo.png',
            sameAs: ['http://j3zb.com/'],
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+86-400-888-6666',
              contactType: 'customer service',
              email: 'hello@j3zb.com',
              areaServed: 'CN',
              availableLanguage: 'Chinese',
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: '望京SOHO',
              addressLocality: '北京市',
              addressRegion: '朝阳区',
              addressCountry: 'CN',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />
    </>
  );
}